import { ReactNode, useState } from 'react';
import {
  Badge,
  Button,
  Col,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  UncontrolledDropdown,
} from 'reactstrap';
import CardInfo from '../../../../components/Cards/CardInfo';
import ModalContent from '../../../../components/Modals/Modal';
import DataTableWithoutHeaderAndPagination from '../../../../components/Core/DataTableWithoutHeaderAndPagination';
import { Link } from 'react-router-dom';
import useCheckMobileScreen from '../../../../hooks/useCheckMobileScreen';
import CountSkeleton from './CountSkeleton';

interface ModalTab {
  key: string;
  name: string;
  statuses: string[];
  showStatus?: boolean;
}

interface HeadlineTile {
  label: string;
  statuses: string[];
  tabKey: string;
}

interface EmployeesOverviewCardInfoProps {
  employees: any[];
  loading: boolean;
  renderActions?: (row: any) => ReactNode;
  onModalToggle?: () => void;
}

const MODAL_TABS: ModalTab[] = [
  { key: 'active', name: 'Active', statuses: ['ACTIVE'] },
  { key: 'loa', name: 'LOA', statuses: ['LOA'] },
  { key: 'pending', name: 'Pending', statuses: ['PENDING', 'OFFER_SENT', 'HIRED'], showStatus: true },
  { key: 'declined', name: 'Declined', statuses: ['DECLINED'] },
  { key: 'terminated', name: 'Terminated', statuses: ['TERMINATED'] },
];

const HEADLINE_TILES: HeadlineTile[] = [
  { label: 'Active', statuses: ['ACTIVE'], tabKey: 'active' },
  { label: 'LOA', statuses: ['LOA'], tabKey: 'loa' },
  { label: 'Pending', statuses: ['PENDING', 'OFFER_SENT', 'HIRED'], tabKey: 'pending' },
  { label: 'Terminated', statuses: ['TERMINATED'], tabKey: 'terminated' },
];

const STATUS_LABELS: Record<string, string> = {
  PENDING: 'Pending Paperwork',
  OFFER_SENT: 'Offer Sent',
  HIRED: 'Hired',
};

const STATUS_DOTS: Record<string, string> = {
  PENDING: 'bg-purple',
  OFFER_SENT: 'bg-teal',
  HIRED: 'bg-yellow',
};

export const EmployeesOverviewCardInfo = ({
  employees,
  loading,
  renderActions,
  onModalToggle,
}: EmployeesOverviewCardInfoProps) => {
  const { isMobile } = useCheckMobileScreen();

  const [toggleModal, setToggleModal] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('active');

  const countStatuses = (statuses: string[]) => employees.filter((e: any) => statuses.includes(e.status)).length || 0;

  const getDataTableByTab = (tab: ModalTab) => {
    const columns: any[] = [
      {
        name: 'Name',
        grow: 3,
        route: (row: any) => `/users/${row?.user?.id}/profile`,
        cell: (row: any) => (
          <Link to={`/users/${row?.user?.id}/profile`} className="d-flex align-items-center min-width-0">
            <div style={{ width: '35px', height: '35px', borderRadius: '50%', overflow: 'hidden', flexShrink: 0 }}>
              <img
                src={row.user.picture}
                alt="User Profile"
                className="img-fluid"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
            <span className="ml-2">{`${row.user.firstName || '-'} ${row.user.lastName || '-'}`}</span>
          </Link>
        ),
      },
    ];

    if (tab.showStatus) {
      columns.push({
        name: 'Status',
        grow: 1,
        cell: (row: any) => (
          <Badge
            color=""
            className="badge-dot badge-lg"
            style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}
          >
            <i className={`${STATUS_DOTS[row.status] || 'bg-purple'} mr-2`} style={{ verticalAlign: 'baseline' }} />
            {STATUS_LABELS[row.status] || String(row.status)?.replace('_', ' ')}
          </Badge>
        ),
      });
    }

    if (renderActions) {
      columns.push({
        name: '',
        grow: 1,
        cell: (row: any) => <div className="w-100 d-flex justify-content-center">{renderActions(row)}</div>,
      });
    }

    return (
      <DataTableWithoutHeaderAndPagination
        title=""
        columns={columns}
        data={employees.filter((e: any) => tab.statuses.includes(e.status))}
        loading={false}
        button={false}
        buttonUrl=""
        onChangeRowsPerPage={(() => {}) as any}
        onChangePage={(() => {}) as any}
      />
    );
  };

  const customStyles = {
    navLink: {
      minHeight: '42px',
      paddingTop: '8px',
      cursor: 'pointer',
    },
  };

  const handleToggleModal = () => {
    setToggleModal(!toggleModal);
    onModalToggle?.();
  };

  const openTab = (tabKey: string) => {
    setActiveTab(tabKey);
    if (!toggleModal) {
      onModalToggle?.();
    }
    setToggleModal(true);
  };

  const activeModalTab = MODAL_TABS.find((t) => t.key === activeTab) || MODAL_TABS[0];

  return (
    <>
      <CardInfo
        class="bg-primary card-info-mg-button"
        title={'Employees Overview'}
        icon="fa-users"
        toggle={handleToggleModal}
      >
        <Row className="mt-4">
          {HEADLINE_TILES.map((tile: HeadlineTile, i: number) => {
            return (
              <div
                className="col text-center pl-1 pr-1"
                style={{
                  ...(i + 1 !== HEADLINE_TILES.length ? { borderRight: '#CCC 1px solid' } : {}),
                  cursor: 'pointer',
                }}
                key={`director-bar-col-${i}`}
                onClick={() => openTab(tile.tabKey)}
              >
                <h2 style={{ fontSize: '25px', fontWeight: 900, color: '#525f7f' }}>
                  {loading ? <CountSkeleton /> : countStatuses(tile.statuses)}
                </h2>
                <p style={{ fontSize: '10px' }}>{tile.label}</p>
              </div>
            );
          })}
        </Row>
      </CardInfo>
      <ModalContent
        title="Employees Overview"
        modal={toggleModal}
        toggle={handleToggleModal}
        size="xl"
        fullscreen={true}
      >
        <Row>
          <Col xs="12">
            {isMobile && (
              <UncontrolledDropdown nav className="w-100 mb-2">
                <DropdownToggle className="p-0" nav>
                  <Button className="w-100">
                    <span>
                      {activeModalTab.name.toLocaleUpperCase()}
                      <span className="badge badge-info ml-1">{countStatuses(activeModalTab.statuses)}</span>
                      <i className="fas fa-chevron-down float-right"></i>
                    </span>
                  </Button>
                </DropdownToggle>
                <DropdownMenu className="dropdown-menu-arrow w-100">
                  {MODAL_TABS.map((tab: ModalTab) => (
                    <DropdownItem
                      key={`dropdown-${tab.key}`}
                      tag={Link}
                      onClick={async (e) => {
                        e.preventDefault();
                        setActiveTab(tab.key);
                      }}
                    >
                      <span>
                        {tab.name}
                        <span className="badge badge-info ml-1">{countStatuses(tab.statuses)}</span>
                      </span>
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            )}
            {!isMobile && (
              <Nav tabs>
                {MODAL_TABS.map((tab: ModalTab, i: number) => (
                  <NavItem className="cursor-pointer" key={`nav-item_${i}`}>
                    <NavLink
                      style={customStyles.navLink}
                      active={activeTab === tab.key}
                      onClick={() => setActiveTab(tab.key)}
                    >
                      <span>
                        {tab.name}
                        <span className="badge badge-light ml-1">{countStatuses(tab.statuses)}</span>
                      </span>
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>
            )}
          </Col>
          <Col xs="12">
            <TabContent activeTab={activeTab}>{getDataTableByTab(activeModalTab)}</TabContent>
          </Col>
        </Row>
      </ModalContent>
    </>
  );
};
